// The conversation of the /transcripts/$id canvas (docs/09a): chat bubbles —
// the agent under test (`assistant`) on the left, the caller (`user`) on the
// right, `tool` calls as a centered mono one-liner. The content the route
// exists for (UI-LAW §4b.2). A bubble seeks the audio to its start; the
// bubble being played carries the now-playing outline; the expanded result's
// locator outlines its evidence and anchors the Mark under the last bubble
// it covers. While a Label draft is active a bubble click picks an
// `utterance_range` (pick wins over seek) and a text selection picks a
// `char_span`; the timestamp stays a dedicated seek target in both modes.

import { useEffect, useRef, useState } from "react";

import type { components } from "../api/schema";
import { formatClock } from "../lib/format";
import type { Verdict } from "../lib/verdictTone";
import { Code, EmptyState } from "../ui";
import { presentToolCall } from "./transcripts/toolCallPresentation";

type TranscriptUtterance = components["schemas"]["TranscriptUtterance"];
type Locator = NonNullable<components["schemas"]["LabelCreate"]["locator"]>;

export interface ConversationAnchor {
  answerId: string;
  index: number;
  name: string;
  // The justification's first sentence, shown after the name.
  summary: string;
  verdict: Verdict | null;
}

export interface TranscriptContentProps {
  anchors?: ConversationAnchor[];
  // Playback position (ms) — the bubble containing it is "now playing".
  currentMs?: number | null;
  isPlaying?: boolean;
  locator?: Locator;
  onAnchorSelect?: (answerId: string) => void;
  onSeek?: (startMs: number) => void;
  onTextPick?: (
    utteranceIndex: number,
    text: string,
    startCodeUnit: number,
    endCodeUnit: number,
  ) => void;
  onUtterancePick?: (utteranceIndex: number) => void;
  // Changes when the selected result changes: the first evidence bubble is
  // scrolled into view once per selection, never on every locator pick.
  scrollKey?: string;
  utterances: TranscriptUtterance[];
}

function speakerName(utterance: TranscriptUtterance): string {
  if (utterance.speaker) {
    return utterance.speaker;
  }
  return utterance.role === "assistant" ? "Agent" : "Caller";
}

function playingIndex(
  utterances: TranscriptUtterance[],
  currentMs: number | null | undefined,
): number {
  if (currentMs == null) {
    return -1;
  }
  return utterances.findIndex(
    (utterance) =>
      utterance.start_ms != null &&
      utterance.end_ms != null &&
      currentMs >= utterance.start_ms &&
      currentMs < utterance.end_ms,
  );
}

export function TranscriptContent({
  anchors = [],
  currentMs,
  isPlaying = false,
  locator,
  onAnchorSelect,
  onSeek,
  onTextPick,
  onUtterancePick,
  scrollKey,
  utterances,
}: TranscriptContentProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const nowPlaying = playingIndex(utterances, currentMs);

  useEffect(() => {
    if (scrollKey == null) {
      return;
    }
    const target = containerRef.current?.querySelector<HTMLElement>(
      ".marker-chat-row--evidence",
    );
    if (target && typeof target.scrollIntoView === "function") {
      target.scrollIntoView({ block: "center" });
    }
  }, [scrollKey]);

  // Follow playback only while playing, and only when the bubble has left
  // the pane's viewport — never fight a reader who scrolled elsewhere.
  useEffect(() => {
    if (!isPlaying || nowPlaying < 0) {
      return;
    }
    const target = containerRef.current?.querySelector<HTMLElement>(
      `[data-index="${String(nowPlaying)}"]`,
    );
    const pane = containerRef.current?.closest<HTMLElement>(
      ".marker-split-pane__pane",
    );
    if (!target || typeof target.scrollIntoView !== "function") {
      return;
    }
    const bounds = target.getBoundingClientRect();
    const viewport = pane
      ? pane.getBoundingClientRect()
      : { bottom: window.innerHeight, top: 0 };
    if (bounds.top < viewport.top || bounds.bottom > viewport.bottom) {
      target.scrollIntoView({ block: "nearest" });
    }
  }, [isPlaying, nowPlaying]);

  if (utterances.length === 0) {
    return <EmptyState title="No transcript content" />;
  }

  const pickerActive = onUtterancePick != null;

  return (
    <div className="marker-transcript-conversation" ref={containerRef}>
      {utterances.map((utterance, index) => {
        const evidence = locatorSelectsUtterance(locator, index);
        const previous = utterances[index - 1];
        const runStart =
          previous == null ||
          previous.role !== utterance.role ||
          previous.speaker !== utterance.speaker;
        const rowAnchors = anchors.filter((anchor) => anchor.index === index);
        const seekable = onSeek != null && utterance.start_ms != null;
        const interactive = pickerActive || seekable;
        const classes = [
          "marker-chat-row",
          `marker-chat-row--${utterance.role}`,
        ];
        if (evidence) {
          classes.push("marker-chat-row--evidence");
        }
        if (index === nowPlaying) {
          classes.push("marker-chat-row--playing");
        }
        if (runStart && utterance.role !== "tool") {
          classes.push("marker-chat-row--run-start");
        }
        const bubbleClasses = ["marker-chat-bubble"];
        if (pickerActive) {
          bubbleClasses.push("marker-chat-bubble--pickable");
        } else if (seekable) {
          bubbleClasses.push("marker-chat-bubble--seekable");
        }
        const activate = () => {
          if (pickerActive) {
            onUtterancePick(index);
          } else if (seekable) {
            onSeek(utterance.start_ms ?? 0);
          }
        };
        return (
          <div
            className={classes.join(" ")}
            data-index={index}
            key={`${utterance.role}-${index.toString()}-${String(
              utterance.start_ms ?? "x",
            )}`}
          >
            {runStart && utterance.role !== "tool" ? (
              <span className="marker-chat-row__name">
                {speakerName(utterance)}
              </span>
            ) : null}
            {utterance.role === "tool" ? (
              <ToolCallCard
                onSeek={seekable ? activate : undefined}
                utterance={utterance}
              />
            ) : (
              <div
                aria-pressed={pickerActive ? evidence : undefined}
                className={bubbleClasses.join(" ")}
                role={interactive ? "button" : undefined}
                tabIndex={interactive ? 0 : undefined}
                onClick={() => {
                  if (window.getSelection()?.isCollapsed !== false) {
                    activate();
                  }
                }}
                onKeyDown={(event) => {
                  if (
                    event.target === event.currentTarget &&
                    (event.key === "Enter" || event.key === " ")
                  ) {
                    event.preventDefault();
                    activate();
                  }
                }}
                onMouseUp={(event) => {
                  if (onTextPick == null) {
                    return;
                  }
                  const offsets = selectionOffsets(
                    event.currentTarget,
                    window.getSelection(),
                  );
                  if (offsets) {
                    onTextPick(
                      index,
                      utterance.text,
                      offsets.start,
                      offsets.end,
                    );
                  }
                }}
              >
                {utterance.text}
              </div>
            )}
            {utterance.start_ms != null && utterance.role !== "tool" ? (
              onSeek ? (
                <button
                  aria-label={`Seek audio to ${formatClock(utterance.start_ms)}`}
                  className="marker-chat-row__time"
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onSeek(utterance.start_ms ?? 0);
                  }}
                >
                  {formatClock(utterance.start_ms)}
                </button>
              ) : (
                <span className="marker-chat-row__time">
                  {formatClock(utterance.start_ms)}
                </span>
              )
            ) : null}
            {rowAnchors.map((anchor) => (
              <button
                className="marker-chat-row__evidence marker-fade-x"
                key={anchor.answerId}
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onAnchorSelect?.(anchor.answerId);
                }}
              >
                <span
                  aria-hidden="true"
                  className={`marker-chat-row__evidence-glyph marker-chat-row__evidence-glyph--${
                    anchor.verdict ?? "none"
                  }`}
                >
                  {anchor.verdict === "fail"
                    ? "✗"
                    : anchor.verdict === "pass"
                      ? "✓"
                      : "·"}
                </span>
                <span className="marker-chat-row__evidence-name">
                  {anchor.name}
                </span>
                {anchor.summary ? (
                  <span className="marker-chat-row__evidence-summary">
                    {anchor.summary}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        );
      })}
    </div>
  );
}

// A `tool` turn: `speaker` is the tool name, `text` whatever the customer
// sent. Header seeks like a bubble; the body is monospace, collapsed to three
// lines with a bottom fade and a `more` toggle.
function ToolCallCard({
  onSeek,
  utterance,
}: {
  onSeek?: () => void;
  utterance: TranscriptUtterance;
}) {
  const [open, setOpen] = useState(false);
  const presentation = presentToolCall(utterance.text);
  const body = [presentation.headline, presentation.body]
    .filter((part) => part != null && part.length > 0)
    .join("\n");
  const header = (
    <>
      <span className="marker-chat-tool__title">
        tool · {utterance.speaker ?? "tool call"}
      </span>
      {utterance.start_ms != null ? (
        <span className="marker-chat-tool__time">
          {formatClock(utterance.start_ms)}
        </span>
      ) : null}
    </>
  );
  return (
    <div className="marker-chat-tool">
      {onSeek ? (
        <button
          aria-label={`Seek audio to ${
            utterance.start_ms == null
              ? "tool call"
              : formatClock(utterance.start_ms)
          }`}
          className="marker-chat-tool__header marker-chat-tool__header--seekable"
          type="button"
          onClick={onSeek}
        >
          {header}
        </button>
      ) : (
        <div className="marker-chat-tool__header">{header}</div>
      )}
      {body.length > 0 ? (
        <>
          <Code
            block
            className={
              open
                ? "marker-chat-tool__body"
                : "marker-chat-tool__body marker-fade-y"
            }
          >
            {body}
          </Code>
          <button
            aria-expanded={open}
            className="marker-chat-tool__toggle"
            type="button"
            onClick={() => {
              setOpen((current) => !current);
            }}
          >
            {open ? "less" : "more"}
          </button>
        </>
      ) : null}
    </div>
  );
}

function locatorSelectsUtterance(
  locator: Locator | undefined,
  index: number,
): boolean {
  if (locator?.kind === "utterance_range") {
    return index >= locator.start_index && index < locator.end_index_exclusive;
  }
  return locator?.kind === "char_span" && locator.utterance_index === index;
}

function selectionOffsets(
  element: Element | null,
  selection: Selection | null,
): { end: number; start: number } | null {
  if (
    element == null ||
    selection == null ||
    selection.isCollapsed ||
    selection.rangeCount === 0
  ) {
    return null;
  }
  const range = selection.getRangeAt(0);
  if (
    !element.contains(range.startContainer) ||
    !element.contains(range.endContainer)
  ) {
    return null;
  }
  const prefix = document.createRange();
  prefix.selectNodeContents(element);
  prefix.setEnd(range.startContainer, range.startOffset);
  const start = prefix.toString().length;
  prefix.setEnd(range.endContainer, range.endOffset);
  return { end: prefix.toString().length, start };
}
